import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  TrendingUp,
  Award,
  Package,
  MessageSquare,
  Target,
  BarChart3,
  Shield,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import { getPlatformStats } from "../lib/api";
import type { PlatformStats } from "../lib/api/stats";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";

interface DiscoverProps {
  onNavigate?: (page: string, productId?: string) => void;
}

export function Discover({ onNavigate }: DiscoverProps) {
  const { profile } = useAuth();
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);

  // No authentication required - page is public
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Fetching platform statistics...');
        
        const stats = await getPlatformStats();

        setPlatformStats(stats);
        
        console.log('✅ Statistics loaded successfully');
      } catch (error: any) {
        console.error("❌ Error fetching statistics:", error);
        toast.error(`Failed to load statistics: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalGradedProducts = platformStats
    ? Object.values(platformStats.gradeDistribution).reduce((a, b) => a + b, 0)
    : 0;

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h1 className="text-4xl sm:text-5xl mb-4">
            Discover Medical Cannabis in the UK
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Explore aggregated insights and trends from our patient community.
            Sign up for personalised product discovery.
          </p>
        </motion.div>

        {/* Platform Stats Overview */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          <motion.div variants={item}>
            <Card className="p-6 glass-card hover-lift">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              {loading ? (
                <Skeleton className="h-10 w-24 mb-2" />
              ) : (
                <div className="text-3xl font-bold mb-1">
                  {platformStats?.totalProducts || 0}
                </div>
              )}
              <p className="text-sm text-muted-foreground">Products in UK Circulation</p>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Card className="p-6 glass-card hover-lift">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-500" />
                </div>
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              {loading ? (
                <Skeleton className="h-10 w-24 mb-2" />
              ) : (
                <div className="text-3xl font-bold mb-1">
                  {platformStats?.totalBatches || 0}
                </div>
              )}
              <p className="text-sm text-muted-foreground">Batch Numbers Tracked</p>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Card className="p-6 glass-card hover-lift">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-amber-500" />
                </div>
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              {loading ? (
                <Skeleton className="h-10 w-24 mb-2" />
              ) : (
                <div className="text-3xl font-bold mb-1">
                  {platformStats?.totalReviews.toLocaleString() || 0}
                </div>
              )}
              <p className="text-sm text-muted-foreground">Patient Reviews</p>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Card className="p-6 glass-card hover-lift">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-500" />
                </div>
                <Sparkles className="w-5 h-5 text-green-500" />
              </div>
              {loading ? (
                <Skeleton className="h-10 w-24 mb-2" />
              ) : (
                <div className="text-3xl font-bold mb-1">
                  {platformStats?.gradeDistribution['A+'] || 0}
                </div>
              )}
              <p className="text-sm text-muted-foreground">A+ Rated Products</p>
            </Card>
          </motion.div>
        </motion.div>

        {/* Grade Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-12"
        >
          <Card className="p-8 glass-card">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6">
              <div>
                <h2 className="text-2xl mb-2">Product Grades</h2>
                <p className="text-muted-foreground">
                  Distribution of {totalGradedProducts} graded products rated by verified patients
                </p>
              </div>
              {!profile?.is_verified_patient && (
                <Button
                  onClick={() => onNavigate?.("verification-hub")}
                  className="gradient-green hover:opacity-90 transition-opacity shadow-lg"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Get Verified to See A+ Products
                </Button>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {[...Array(7)].map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {Object.entries(platformStats?.gradeDistribution || {}).map(([grade, count]) => (
                  <div
                    key={grade}
                    className="text-center p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="text-3xl font-bold mb-1">{count}</div>
                    <Badge
                      variant="outline"
                      className={
                        grade.startsWith('A')
                          ? 'text-primary border-primary'
                          : grade.startsWith('B')
                          ? 'text-blue-600 border-blue-600'
                          : grade.startsWith('C')
                          ? 'text-yellow-600 border-yellow-600'
                          : 'text-muted-foreground'
                      }
                    >
                      Grade {grade}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </motion.div>

        {/* CTA for Non-Authenticated Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center"
        >
          <p className="text-lg text-muted-foreground mb-4">
            Are you a UK medical cannabis patient? Create an account to join the transparency movement!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => onNavigate?.("signup")}
              size="lg"
              className="gradient-green hover:opacity-90 transition-opacity shadow-lg"
            >
              Create Account
            </Button>
            <Button
              onClick={() => onNavigate?.("signin")}
              size="lg"
              variant="outline"
            >
              Sign In
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}